/**
 * Logging del lado del cliente.
 *
 * Existe por una razón concreta: cuando alguien dice "se rompió algo", lo único
 * que permite investigarlo es poder atar lo que vio en pantalla con la traza del
 * servidor. El backend devuelve un `x-request-id` en cada respuesta y lo escribe
 * en su propio log; acá se loguea el MISMO id. Sin eso, un error de pantalla y
 * una traza de servidor son dos hechos que nunca se encuentran.
 *
 * Tres reglas que no se negocian:
 *
 * 1. **Nunca sale un secreto ni un token.** No alcanza con "acordarse": hay un
 *    redactor que recorre el contexto y reemplaza por `[redacted]` cualquier
 *    clave que se llame como un secreto, más los `Bearer …` que aparezcan en un
 *    string. Es el espejo del processor de structlog del backend
 *    (`secret|password|token|authorization|ciphertext`), y lo verifica
 *    `log.test.ts`.
 * 2. **En desarrollo se lee, en producción se parsea.** Dev imprime una línea
 *    corta y coloreada con el contexto expandido por la consola; producción
 *    emite UNA línea JSON por evento, que es lo que un colector sabe ingerir.
 * 3. **El nivel es de entorno, no de código.** `VITE_LOG_LEVEL` (o
 *    `window.__APP_CONFIG__.LOG_LEVEL`) decide; el default es `debug` en dev e
 *    `info` en producción. Bajar el ruido no debería requerir un deploy con el
 *    código cambiado.
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error' | 'silent'

const ORDER: Record<LogLevel, number> = {
  debug: 10,
  info: 20,
  warn: 30,
  error: 40,
  silent: 100,
}

export type LogContext = Record<string, unknown>

/* ------------------------------------------------------------- redacción - */

/**
 * Claves cuyo VALOR nunca se imprime. La comparación es por substring en
 * minúsculas para que `secret_ciphertext`, `Authorization` o `id_token` caigan
 * igual que `secret`.
 */
const SENSITIVE_KEY = /secret|password|passwd|token|authorization|ciphertext|credential|api_?key/i

/** `Bearer eyJ…` incrustado en un string (una URL, un mensaje de error). */
const BEARER = /\bBearer\s+[\w\-._~+/]+=*/gi

/**
 * El secreto serializado DENTRO de un string.
 *
 * Es el caso que más se escapa: un `Error` cuyo mensaje trae el body que se
 * intentó guardar (`no se pudo guardar {"secret":"..."}`), o una query string
 * con `?token=...`. Recorrer las claves del objeto no alcanza, porque acá ya no
 * hay objeto: hay texto.
 */
const INLINE_SECRET =
  /("?(?:secret|password|passwd|token|authorization|ciphertext|credential|api_?key)[\w-]*"?\s*[:=]\s*)("[^"]*"|'[^']*'|[^\s,&}]+)/gi

export const REDACTED = '[redacted]'

function redactString(value: string): string {
  return value
    .replace(BEARER, `Bearer ${REDACTED}`)
    .replace(INLINE_SECRET, (_match, prefix: string) => `${prefix}"${REDACTED}"`)
}

/**
 * Copia el contexto reemplazando todo lo que huela a secreto.
 *
 * Recorre en profundidad porque el dato peligroso casi nunca está en el primer
 * nivel: viene dentro del body de una mutación fallida o del payload de un
 * error.
 */
export function redact(value: unknown, depth = 0): unknown {
  if (depth > 6) return '[depth-limit]'
  if (typeof value === 'string') return redactString(value)
  if (value === null || typeof value !== 'object') return value
  if (value instanceof Error) {
    return { name: value.name, message: redactString(value.message) }
  }
  if (Array.isArray(value)) return value.map((item) => redact(item, depth + 1))

  const out: Record<string, unknown> = {}
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    out[key] = SENSITIVE_KEY.test(key) ? REDACTED : redact(item, depth + 1)
  }
  return out
}

/* ------------------------------------------------------------------ nivel - */

function envValue(name: string): string | undefined {
  const fromRuntime = (globalThis as { __APP_CONFIG__?: Record<string, string | undefined> })
    .__APP_CONFIG__?.[name]
  if (fromRuntime && !fromRuntime.startsWith('${')) return fromRuntime

  const env = import.meta.env as unknown as Record<string, string | undefined>
  return env[`VITE_${name}`]
}

function isDev(): boolean {
  return Boolean(import.meta.env.DEV)
}

function defaultLevel(): LogLevel {
  const configured = envValue('LOG_LEVEL')?.toLowerCase()
  if (configured && configured in ORDER) return configured as LogLevel
  return isDev() ? 'debug' : 'info'
}

let level: LogLevel = defaultLevel()

export function setLogLevel(next: LogLevel): void {
  level = next
}

export function getLogLevel(): LogLevel {
  return level
}

/** Solo para tests: vuelve a leer el entorno. */
export function resetLogLevel(): void {
  level = defaultLevel()
}

/* ------------------------------------------------------------------ sink - */

export interface LogRecord {
  level: Exclude<LogLevel, 'silent'>
  event: string
  time: string
  context: LogContext
}

type Sink = (record: LogRecord) => void

/* eslint-disable no-console -- este módulo ES la única puerta a la consola */
const CONSOLE: Record<Exclude<LogLevel, 'silent'>, (...args: unknown[]) => void> = {
  debug: (...args) => console.debug(...args),
  info: (...args) => console.info(...args),
  warn: (...args) => console.warn(...args),
  error: (...args) => console.error(...args),
}
/* eslint-enable no-console */

function consoleSink(record: LogRecord): void {
  const write = CONSOLE[record.level]

  if (!isDev()) {
    // Producción: UNA línea JSON. Un colector no sabe leer una tabla bonita.
    write(
      JSON.stringify({
        level: record.level,
        event: record.event,
        time: record.time,
        ...record.context,
      }),
    )
    return
  }

  const { request_id: requestId, ...rest } = record.context
  const tag = requestId ? `${record.event} (${String(requestId)})` : record.event
  if (Object.keys(rest).length === 0) write(`[${record.level}] ${tag}`)
  else write(`[${record.level}] ${tag}`, rest)
}

let sink: Sink = consoleSink

/** Los tests inyectan su propio sink para poder afirmar sobre lo emitido. */
export function setLogSink(next: Sink | null): void {
  sink = next ?? consoleSink
}

/* ------------------------------------------------------------------- api - */

function emit(recordLevel: Exclude<LogLevel, 'silent'>, event: string, context?: LogContext): void {
  if (ORDER[recordLevel] < ORDER[level]) return

  sink({
    level: recordLevel,
    event,
    time: new Date().toISOString(),
    context: (redact(context ?? {}) as LogContext) ?? {},
  })
}

export const log = {
  debug: (event: string, context?: LogContext) => emit('debug', event, context),
  info: (event: string, context?: LogContext) => emit('info', event, context),
  warn: (event: string, context?: LogContext) => emit('warn', event, context),
  error: (event: string, context?: LogContext) => emit('error', event, context),
}

export type Logger = typeof log
